/** SUNTRONIX ONE — Customer-Owned Release 2: billing candidates, storage
 *  charge configs, monthly customer-owned report. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Settings2, Calculator } from "lucide-react";
import { Modal, Field } from "./StockInventoryDialogs";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const STATUS_STYLE = {
  READY_TO_BILL: "bg-emerald-100 text-emerald-800",
  RATE_REQUIRED: "bg-amber-100 text-amber-800",
  BILLED: "bg-blue-100 text-blue-800",
  WAIVED: "bg-slate-200 text-slate-600",
};
const fmtM = (v) => (v || v === 0) ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function BillingTab() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [status, setStatus] = useState("");
  const [configs, setConfigs] = useState([]);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [month, setMonth] = useState(thisMonth());

  const load = useCallback(() => {
    api.get("/inventory/customer-owned/billing/candidates",
      { params: status ? { status } : {} })
      .then(({ data }) => { setItems(data.items || []); setTotals(data.totals || {}); });
    api.get("/inventory/customer-owned/billing/configs")
      .then(({ data }) => setConfigs(data.items || []));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    const [y, m] = month.split("-").map(Number);
    try {
      const { data } = await api.post("/inventory/customer-owned/billing/compute-storage",
        { year: y, month: m });
      toast.success(`Storage computed for ${data.period} — ${data.computed} customer(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Compute failed"); }
  };
  const setRate = async (c) => {
    const v = window.prompt(`Rate for ${c.code} (${c.service_name}) × ${c.qty}:`);
    if (!v || isNaN(Number(v))) return;
    try {
      await api.patch(`/inventory/customer-owned/billing/candidates/${c.id}`, { rate: Number(v) });
      toast.success("Rate set — READY TO BILL"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markBilled = async (c) => {
    const ref = window.prompt(`Invoice reference for ${c.code}:`) || "";
    try {
      await api.post(`/inventory/customer-owned/billing/candidates/${c.id}/mark-billed`,
        { invoice_ref: ref });
      toast.success("Marked BILLED"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const waive = async (c) => {
    if (!window.confirm(`Waive ${c.code}?`)) return;
    try {
      await api.post(`/inventory/customer-owned/billing/candidates/${c.id}/waive`);
      toast.success("Waived"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="co-billing-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="cob-status-filter" className="text-[11px] border border-slate-300 rounded-md px-2 py-1.5">
          <option value="">All statuses</option>
          {["READY_TO_BILL", "RATE_REQUIRED", "BILLED", "WAIVED"].map((s) =>
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <span className="text-[11px] text-slate-500">
          Ready ₹{fmtM(totals.READY_TO_BILL || 0)} · Billed ₹{fmtM(totals.BILLED || 0)}</span>
        <div className="flex-1" />
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            data-testid="cob-month" className="text-[11px] border border-slate-300 rounded-md px-2 py-1.5" />
        <button onClick={compute} data-testid="cob-compute-storage"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
          <Calculator className="h-3 w-3" /> Compute storage</button>
        <button onClick={() => setCfgOpen(true)} data-testid="cob-configure"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <Settings2 className="h-3 w-3" /> Charge configs ({configs.length})</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Candidate</th>
            <th className="text-left px-3 py-2">Customer</th>
            <th className="text-left px-3 py-2">Type / Description</th>
            <th className="text-left px-3 py-2">Ref</th>
            <th className="text-right px-3 py-2">Qty</th>
            <th className="text-right px-3 py-2">Rate</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-left px-3 py-2">Period</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2 w-40"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} className="text-center text-slate-400 px-3 py-8">
              No billing candidates yet — they generate on fulfilment delivery and storage compute.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`cob-row-${c.code}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{c.code}</td>
                <td className="px-3 py-2">{c.customer_name}</td>
                <td className="px-3 py-2">
                  <span className="text-[8.5px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 mr-1">{c.type}</span>
                  {c.service_name}
                  {c.detail && <div className="text-[9px] text-slate-400">
                    {c.detail.mode} · {c.detail.unit_days} unit-day(s) · {c.detail.units} unit(s)</div>}
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{c.fulfilment_code || "—"}
                  {c.employee_name && <div className="text-[9px] text-slate-400">{c.employee_name}</div>}</td>
                <td className="px-3 py-2 text-right font-mono">{c.qty}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtM(c.rate)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmtM(c.amount)}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{c.period}</td>
                <td className="px-3 py-2">
                  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " + (STATUS_STYLE[c.status] || "")}>
                    {c.status.replace(/_/g, " ")}</span>
                  {c.invoice_ref && <div className="text-[9px] text-slate-400">{c.invoice_ref}</div>}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  {c.status === "RATE_REQUIRED" && (
                    <button onClick={() => setRate(c)} data-testid={`cob-rate-${c.code}`}
                        className="text-[10px] px-2 py-1 rounded border border-amber-400 text-amber-700">Set rate</button>)}
                  {c.status === "READY_TO_BILL" && (
                    <button onClick={() => markBilled(c)} data-testid={`cob-bill-${c.code}`}
                        className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">Mark billed</button>)}
                  {["READY_TO_BILL", "RATE_REQUIRED"].includes(c.status) && (
                    <button onClick={() => waive(c)} data-testid={`cob-waive-${c.code}`}
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 text-slate-500">Waive</button>)}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {cfgOpen && <ConfigDialog configs={configs} onClose={() => setCfgOpen(false)}
          onDone={() => { setCfgOpen(false); load(); }} />}
    </div>
  );
}

function ConfigDialog({ configs, onClose, onDone }) {
  const [customers, setCustomers] = useState([]);
  const [c, setC] = useState({ customer_id: "", customer_name: "", storage_mode: "NONE",
    storage_rate: "", currency: "INR", free_days: 0,
    autopilot_charge_per_device: "", handling_charge_per_fulfilment: "" });
  useEffect(() => {
    api.get("/inventory/customers", { params: { q: "" } })
      .then(({ data }) => setCustomers(data.items || []));
  }, []);
  const pick = (id) => {
    const cust = customers.find((x) => x.id === id);
    const ex = configs.find((x) => x.customer_id === id);
    setC(ex ? { ...ex, storage_rate: ex.storage_rate ?? "", free_days: ex.free_days ?? 0,
      autopilot_charge_per_device: ex.autopilot_charge_per_device ?? "",
      handling_charge_per_fulfilment: ex.handling_charge_per_fulfilment ?? "" }
      : { ...c, customer_id: id, customer_name: cust?.name || "" });
  };
  const save = async () => {
    if (!c.customer_id) { toast.error("Pick a customer"); return; }
    try {
      await api.put("/inventory/customer-owned/billing/config", {
        customer_id: c.customer_id, customer_name: c.customer_name,
        storage_mode: c.storage_mode,
        storage_rate: c.storage_rate === "" ? null : Number(c.storage_rate),
        currency: c.currency || "INR", free_days: Number(c.free_days) || 0,
        autopilot_charge_per_device: c.autopilot_charge_per_device === "" ? null : Number(c.autopilot_charge_per_device),
        handling_charge_per_fulfilment: c.handling_charge_per_fulfilment === "" ? null : Number(c.handling_charge_per_fulfilment) });
      toast.success("Charge config saved"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Modal title="Customer charge configuration" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="cob-config-dialog">
        <Field label="Customer" required>
          <select value={c.customer_id} onChange={(e) => pick(e.target.value)}
              data-testid="cob-cfg-customer" className={inputCls}>
            <option value="">— pick —</option>
            {customers.map((x) => <option key={x.id} value={x.id}>{x.name}
              {configs.some((g) => g.customer_id === x.id) ? " ✓" : ""}</option>)}
          </select></Field>
        <Field label="Storage mode">
          <select value={c.storage_mode} onChange={(e) => setC({ ...c, storage_mode: e.target.value })}
              data-testid="cob-cfg-mode" className={inputCls}>
            {["NONE", "PER_UNIT_DAILY", "PER_UNIT_MONTHLY", "FLAT_MONTHLY"].map((m) =>
              <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Storage rate">
          <input type="number" value={c.storage_rate} data-testid="cob-cfg-rate"
              onChange={(e) => setC({ ...c, storage_rate: e.target.value })} className={inputCls} /></Field>
        <Field label="Free days (from receipt)">
          <input type="number" value={c.free_days} data-testid="cob-cfg-freedays"
              onChange={(e) => setC({ ...c, free_days: e.target.value })} className={inputCls} /></Field>
        <Field label="Autopilot charge / device">
          <input type="number" value={c.autopilot_charge_per_device} data-testid="cob-cfg-autopilot"
              onChange={(e) => setC({ ...c, autopilot_charge_per_device: e.target.value })} className={inputCls} /></Field>
        <Field label="Handling charge / fulfilment">
          <input type="number" value={c.handling_charge_per_fulfilment} data-testid="cob-cfg-handling"
              onChange={(e) => setC({ ...c, handling_charge_per_fulfilment: e.target.value })} className={inputCls} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Close</button>
        <button onClick={save} data-testid="cob-cfg-save"
            className="text-[11px] px-3 py-1.5 rounded-md bg-blue-800 text-white">Save config</button>
      </div>
    </Modal>
  );
}

export function MonthlyReportTab() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/inventory/customers", { params: { q: "" } })
      .then(({ data }) => setCustomers(data.items || []));
  }, []);
  useEffect(() => {
    if (!customerId) { setD(null); return; }
    const [y, m] = month.split("-").map(Number);
    api.get("/inventory/customer-owned/report",
      { params: { customer_id: customerId, year: y, month: m } })
      .then(({ data }) => setD(data)).catch(() => setD(null));
  }, [customerId, month]);
  const dl = async (fmt) => {
    const [y, m] = month.split("-").map(Number);
    try {
      const res = await api.get(`/inventory/customer-owned/report/${fmt}`,
        { params: { customer_id: customerId, year: y, month: m }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `customer-owned-${month}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="space-y-4" data-testid="co-report-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            data-testid="cor-customer" className="text-[11px] border border-slate-300 rounded-md px-2 py-1.5">
          <option value="">— pick customer —</option>
          {customers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            data-testid="cor-month" className="text-[11px] border border-slate-300 rounded-md px-2 py-1.5" />
        {d && <>
          <button onClick={() => dl("pdf")} data-testid="cor-pdf"
              className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
            <FileDown className="h-3 w-3" /> PDF</button>
          <button onClick={() => dl("xlsx")} data-testid="cor-xlsx"
              className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
            <FileDown className="h-3 w-3" /> XLSX</button>
        </>}
      </div>
      {!customerId && <div className="text-[11.5px] text-slate-400 border border-dashed border-slate-300 rounded-lg p-8 text-center">
        Pick a customer and month to build the customer-owned monthly report.</div>}
      {d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="cor-summary">
            {Object.entries(d.summary).map(([k, v]) => (
              <div key={k} className="border border-slate-200 rounded-lg bg-white p-2.5">
                <div className="text-[9px] uppercase tracking-wide text-slate-400">{k.replace(/_/g, " ")}</div>
                <div className="text-lg font-semibold font-mono">
                  {k === "charges_total" ? `₹${fmtM(v)}` : v}</div>
              </div>))}
          </div>
          <ReportSection title="Receipts" cols={["Code", "Product", "Date", "Qty"]}
              rows={d.receipts.map((r) => [r.code, r.product, r.date, r.qty])} />
          <ReportSection title="Fulfilments delivered" cols={["Code", "Employee", "Date", "Devices", "Services"]}
              rows={d.fulfilments.map((f) => [f.code, f.employee, f.date,
                f.devices.filter(Boolean).join(", "), f.services.join(", ")])} />
          <ReportSection title="Holdings at period end" cols={["Serial", "Product", "Status", "Held since"]}
              rows={d.holdings.map((h2) => [h2.serial, h2.product, h2.status, h2.since])} />
          <ReportSection title="Charges" cols={["Code", "Type", "Description", "Qty", "Rate", "Amount", "Status"]}
              rows={d.charges.map((cc) => [cc.code, cc.type, cc.description, cc.qty,
                fmtM(cc.rate), fmtM(cc.amount), cc.status])} />
        </div>)}
    </div>
  );
}

function ReportSection({ title, cols, rows }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
      <div className="px-3 py-2 text-[11px] font-semibold border-b border-slate-100">{title}
        <span className="text-slate-400 font-normal ml-1">({rows.length})</span></div>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          {cols.map((c) => <th key={c} className="text-left px-3 py-1.5">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length}
            className="text-center text-slate-400 px-3 py-4">None in this period.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((v, j) => <td key={j} className="px-3 py-1.5">{v ?? "—"}</td>)}
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}
